import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ComponentEnum,
  IUserRecord,
  splitUserRecordListValue,
} from "../../user_records/UserRecordsModels";
import { queryUserRecords } from "../../user_records/UserRecordsAPI";
import { RootState } from "../Store";
import { TrackerNamesMap } from "../../sites/SiteModels";
import { tryGetTrackerName } from "../../trackers/TrackerName";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "./RequestState";
import { LoadingState } from "../LoadingState";

interface IUserRecordsState {
  [key: string]: {
    rawRecords: IUserRecord[];
    error?: string;
    loading: boolean;
    request?: RequestState;
  };
}

const initialState: IUserRecordsState = {};

export const multiSitesUserRecordsSlice = createSlice({
  name: "multiSitesUserRecords",
  initialState,
  reducers: {
    addRecord: (state, action: PayloadAction<{ site: string; record: IUserRecord }>) => {
      const { site, record } = action.payload;
      if (!state[site]) {
        state[site] = { rawRecords: [], loading: false };
      }
      state[site].rawRecords.push(record);
    },
    updateRecord: (state, action: PayloadAction<{ site: string; record: IUserRecord }>) => {
      const { site, record } = action.payload;
      if (!state[site]) {
        state[site] = { rawRecords: [record], loading: false };
        return;
      }

      state[site].rawRecords = state[site].rawRecords.map((existingRecord) =>
        existingRecord.id === record.id ? record : existingRecord
      );
    },
    deleteRecord: (state, action: PayloadAction<{ site: string; recordId: number }>) => {
      const { site, recordId } = action.payload;
      if (state[site]) {
        state[site].rawRecords = state[site].rawRecords.filter((record) => record.id !== recordId);
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchUserRecords.fulfilled, (state, action) => {
        const { siteId, records } = action.payload;
        const current = state[siteId];
        if (!current?.request || !completeRequest(current.request, action.meta.requestId)) {
          return;
        }
        current.rawRecords = records;
        current.loading = false;
        current.error = undefined;
      })
      .addCase(fetchUserRecords.rejected, (state, action) => {
        const { siteId } = action.meta.arg;
        const current = state[siteId];
        if (current?.request && rejectRequest(current.request, action)) {
          current.loading = false;
          if (!action.meta.aborted) {
            current.error = "Failed to load user records";
          }
        }
      })
      .addCase(fetchUserRecords.pending, (state, action) => {
        const { siteId } = action.meta.arg;
        const current = (state[siteId] ??= { rawRecords: [], loading: false });
        current.request ??= { loadingState: LoadingState.None };
        beginRequest(current.request, action.meta.requestId);
        current.loading = true;
      }),
});

type FetchUserRecordsArgs = {
  siteId: string;
  creationTimeStart: number;
  creationTimeEnd: number;
};

export const fetchUserRecords = createAsyncThunk(
  "multiSitesUserRecords/fetchUserRecords",
  async ({ siteId, creationTimeStart, creationTimeEnd }: FetchUserRecordsArgs) => {
    const records = await queryUserRecords(
      siteId,
      new Date(creationTimeStart),
      new Date(creationTimeEnd)
    );
    return { siteId, records };
  }
);

function formatUserRecord(record: IUserRecord, trackerNamesMap: TrackerNamesMap): IUserRecord {
  if (record.componentType !== ComponentEnum.Tracker || !record.componentName) {
    return record;
  }
  try {
    const trackerNames = splitUserRecordListValue(record.componentName)
      .map((id: string) => ({ number: parseInt(id, 10), original: id }))
      .map(({ number, original }) =>
        isNaN(number) ? original : tryGetTrackerName(number, trackerNamesMap)
      );
    if (trackerNames.length == 1) {
      return {
        ...record,
        componentName: trackerNames[0],
      };
    }
    return {
      ...record,
      componentName: trackerNames.join(", "),
    };
  } catch (e) {
    console.error("Failed to format user record", e);
    return record;
  }
}

export const selectCurrentSiteFormattedUserRecords = createSelector(
  [
    (state: RootState) => state.site.trackers,
    (state: RootState) => state.multiSitesUserRecords[state.site.siteId],
  ],
  (trackerNamesMap, userRecordsState) => {
    if (!userRecordsState || userRecordsState.loading) {
      return [];
    }
    const trackerNames = trackerNamesMap ?? {};
    return userRecordsState.rawRecords.map((record) => formatUserRecord(record, trackerNames));
  }
);

export const multiSitesUserRecordsReducer = multiSitesUserRecordsSlice.reducer;
